import React, { FC, useState } from 'react';
import { Formik, Form, Field, FieldProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { DialogTitleWithClose, Loader } from 'ui';
import {
  PortfolioErrorType,
  portfolioErrorMessages,
} from 'shared/constants/portfolio/portfolioErrorTypes';
import { PortfolioItem, SelectedEntities } from 'shared/types/portfolios';

import { useCreatePortfolio, useUpdatePortfolio } from 'modules/common/GraphQL/portfolios/hooks';
import { PortfolioAction } from '../PortfolioManager/types';
import { useVariantAction } from './portfolioModalUtils';
import { getResponseMessage } from '../GraphQL/utils';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    paddingTop: '0 !important',
  },
  input: {
    marginTop: 5,
  },
  helperText: {
    minWidth: theme.spacing(60),
    color: theme.palette.error.dark,
  },
}));

type CreateNewPortfolioModalProps = {
  open: boolean;
  onClose: () => void;
  onCreate?: (id: string, name: string, description: string) => void;
  portfolio?: { id: string; name: string; description: string };
  itemsData?: SelectedEntities;
  itemType?: PortfolioItem;
  actionVariant?: PortfolioAction;
  sourcePortfolioId?: string;
};

type Error = {
  type: PortfolioErrorType;
  message: string;
};

type AddToNewPortfolioResponse = {
  result: {
    data: any;
    errors: {
      message: string;
    }[];
  };
};

type FormData = {
  name: string;
  description: string;
};

type CreateResponse = {
  createPortfolio?: {
    portfolio: {
      id: string;
      name: string;
      description: string;
    };
  };
};

type UpdateResponse = {
  updatePortfolio?: {
    portfolio: {
      id: string;
      name: string;
      description: string;
    };
  };
};

export const CreateNewPortfolioModal: FC<CreateNewPortfolioModalProps> = ({
  open,
  onClose,
  onCreate,
  portfolio,
  itemsData = {
    count: 0,
    itemIds: {},
    items: {},
    type: null,
  },
  itemType = 'ENTITY',
  actionVariant = 'add',
  sourcePortfolioId = '',
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [showError, setShowError] = useState<Error | null>(null);
  const [isPending, setIsPending] = useState(false);
  const createPortfolio = useCreatePortfolio();
  const updatePortfolio = useUpdatePortfolio();
  const { error, success } = useSnackbar();

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(1)
      .max(100)
      .trim(t('PORTFOLIO.PORTFOLIO_NAME_WHITESPACE_WARNING'))
      .strict(true)
      .required(),
    description: Yup.string()
      .min(1)
      .max(4000),
  });

  const { action, itemSubmitSuccess } = useVariantAction(itemsData, itemType, sourcePortfolioId)[
    actionVariant
  ];

  const handleSubmit = async (payload: FormData) => {
    setIsPending(true);

    if (portfolio?.id) {
      try {
        const response = await updatePortfolio({
          portfolioId: portfolio?.id,
          portfolioName: payload.name,
          portfolioDescription: payload.description,
        });

        const updatedPortfolio = (response?.data as UpdateResponse).updatePortfolio?.portfolio;
        if (onCreate && updatedPortfolio) {
          onCreate(updatedPortfolio.id, updatedPortfolio.name, updatedPortfolio.description);
        }
        onClose();
      } catch {
        setShowError({
          type: PortfolioErrorType.CREATE_NAME_CONFLICT,
          message: portfolioErrorMessages[PortfolioErrorType.CREATE_NAME_CONFLICT],
        });
      }
      setIsPending(false);
    } else {
      try {
        const response = await createPortfolio({
          name: payload.name,
          description: payload.description,
        });

        const newPortfolio = (response?.data as CreateResponse).createPortfolio?.portfolio;

        if (newPortfolio && itemsData && itemsData.count) {
          try {
            await action(newPortfolio.id);
            success(getResponseMessage(null, itemSubmitSuccess));
          } catch (err) {
            const { networkError } = err as any;
            const {
              data: responseData,
              errors,
            } = (networkError as AddToNewPortfolioResponse)?.result;

            error(
              getResponseMessage(
                responseData,
                errors
                  ? errors.map(item => item.message).join('<br />')
                  : t('PORTFOLIO.UNABLE_TO_ADD_TO_NEW_PORTFOLIO'),
              ),
            );
          }
          setIsPending(false);
          onClose();
        } else {
          success(t('PORTFOLIO.NEW_PORTFOLIO_CREATED'));
          if (onCreate && newPortfolio) {
            onCreate(newPortfolio.id, newPortfolio.name, newPortfolio.description);
          }
          onClose();
        }
      } catch (err) {
        const { networkError } = err as any;
        const { data: responseData, errors } = (networkError as AddToNewPortfolioResponse)?.result;

        error(
          getResponseMessage(
            responseData,
            errors
              ? errors.map(item => item.message).join('<br />')
              : t('PORTFOLIO.UNABLE_TO_ADD_TO_NEW_PORTFOLIO'),
          ),
        );
      }
      setIsPending(false);
    }
  };

  const initialValues: FormData = {
    name: portfolio?.name || '',
    description: portfolio?.description || '',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}>
      <DialogTitleWithClose onClose={onClose}>
        {portfolio?.id ? t('PORTFOLIO.UPDATE_PORTFOLIO') : t('PORTFOLIO.CREATE_PORTFOLIO')}
      </DialogTitleWithClose>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ errors, touched }) => (
          <Form>
            <DialogContent className={classes.dialogContent}>
              <Field name="name">
                {({ field }: FieldProps) => (
                  <>
                    <TextField
                      {...field}
                      className={classes.input}
                      type="text"
                      label={t('PORTFOLIO.CREATE_PORTFOLIO_NAME')}
                      error={
                        (touched.name && !!errors.name) ||
                        showError?.type === PortfolioErrorType.CREATE_NAME_CONFLICT
                      }
                      helperText={showError && t(showError.message)}
                      onBlur={() => showError && setShowError(null)}
                      fullWidth
                    />
                    <FormHelperText className={classes.helperText}>{errors.name}</FormHelperText>
                  </>
                )}
              </Field>
              <Field name="description">
                {({ field }: FieldProps) => (
                  <TextField
                    {...field}
                    type="text"
                    label={t('PORTFOLIO.CREATE_PORTFOLIO_DESCRIPTION')}
                    error={touched.description && !!errors.description}
                    fullWidth
                    multiline
                    rows={10}
                  />
                )}
              </Field>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={onClose}>
                {t('CANCEL')}
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={isPending}>
                {portfolio?.id ? t('UPDATE') : t('CREATE')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
      {isPending && <Loader cover />}
    </Dialog>
  );
};
